import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// Always use current origin to prevent CORS with custom domains
const API = typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('token'));

  const fetchUser = useCallback(async (authToken) => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setUser(res.data);
      // Sync localStorage so isDemoUser() and other readers stay current
      localStorage.setItem('user', JSON.stringify(res.data));
      // Set default header for future requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
    } catch (e) {
      // Token invalid, clear it
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      delete axios.defaults.headers.common['Authorization'];
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      fetchUser(storedToken);
    } else {
      setLoading(false);
    }
  }, [fetchUser]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    // Fetch full user data including preferences
    await fetchUser(newToken);
    return userData;
  };

  const register = async (email, password, name) => {
    await axios.post(`${API}/auth/register`, { email, password, name });
    return login(email, password);
  };

  const softSignup = async (email, intent = "kennel", name = "") => {
    const res = await axios.post(`${API}/auth/soft-signup`, { email, name, intent });
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    await fetchUser(newToken);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const updateUserPreference = (key, value) => {
    setUser(prev => prev ? { ...prev, [key]: value } : null);
  };

  const refreshUser = async () => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      await fetchUser(storedToken);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, softSignup, logout, token, updateUserPreference, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
